import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { MapPin } from "lucide-react";

const Stat = ({ label, value, sub, testid, accent }) => (
  <div className={`rounded-xl px-3 py-2 ${accent || "bg-zinc-50"}`} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className="font-heading text-lg font-bold leading-tight">{value}</div>
    {sub && <div className="text-[11px] text-zinc-500">{sub}</div>}
  </div>
);

// Statistiche → Avvisi di prossimità: passaggi vicino alle sedi e acquisti entro 48h
export default function ProximityStatsCard({ days = 30, tooltipStyle }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/stats/proximity", { params: { days } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [days]);
  if (!d) return null;
  const daily = (d.daily || []).map((x) => ({ ...x, label: new Date(x.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) }));
  return (
    <div className="card-elev p-5" data-testid="proximity-stats-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-semibold text-lg">Avvisi di prossimità</h3>
          <p className="text-xs text-zinc-500">Clienti passati vicino alle sedi (web app) negli ultimi {d.days} giorni e acquisti entro 48 ore dall'avviso</p>
        </div>
        <MapPin className="h-5 w-5 text-zinc-500" />
      </div>
      {!d.total_hits ? (
        <div className="text-sm text-zinc-500 py-6 text-center" data-testid="proximity-stats-empty">Nessun passaggio rilevato nel periodo: gli avvisi partono quando un cliente apre la web app entro il raggio di una sede.</div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 grid grid-cols-2 gap-2 content-start">
            <Stat label="Avvisi inviati" value={d.total_hits} testid="prox-stat-hits" />
            <Stat label="Clienti nei paraggi" value={d.customers} testid="prox-stat-customers" />
            <Stat label="Hanno acquistato" value={d.converted} sub={`${d.rate}% dei clienti avvisati`} testid="prox-stat-converted" accent="bg-emerald-50" />
            <Stat label="Incasso collegato" value={`€ ${Number(d.eur).toFixed(2)}`} sub="transazioni entro 48h" testid="prox-stat-eur" accent="bg-amber-50" />
            <div className="col-span-2 rounded-xl border border-zinc-200 overflow-hidden" data-testid="prox-branches-table">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-500"><tr><th className="text-left p-2">Sede</th><th className="text-right p-2">Avvisi</th><th className="text-right p-2">Clienti</th><th className="text-right p-2">Acquisti</th><th className="text-right p-2">Conv.</th></tr></thead>
                <tbody>
                  {d.branches.map((b) => (
                    <tr key={b.branch_id} className="border-t border-zinc-100" data-testid={`prox-branch-${b.branch_id}`}>
                      <td className="p-2 font-medium">{b.branch}</td><td className="p-2 text-right">{b.hits}</td><td className="p-2 text-right">{b.customers}</td>
                      <td className="p-2 text-right text-emerald-700 font-semibold">{b.converted}</td><td className="p-2 text-right">{b.rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="lg:col-span-3 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} /><Legend />
                <Bar dataKey="hits" name="Passaggi" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="converted" name="Acquisti entro 48h" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
